import { useMemo } from "react";
import { generatePath, useNavigate } from "react-router-dom";

import {
  Link,
  ShimmeredDetailsListProps,
  Stack,
  Text,
  TextBadge,
  TextBadgeColor,
  TextBadgeSize,
  useTheme
} from "@bps/fluent-ui";
import { DataTable, DataTableColumn } from "@components/tables/DataTable";

import { DeploymentRingResultantVersion } from "./deploymentRingResultantVersion.utils";

const ROLLOUT_PATH =
  "/system/plt/deployment-rings/:deploymentRingId/rollouts/:rolloutId";

export interface SoftwarePackageDeploymentRingsTableProps
  extends ShimmeredDetailsListProps {
  items: DeploymentRingResultantVersion[];
}

export const SoftwarePackageDeploymentRingsTable = ({
  ...props
}: SoftwarePackageDeploymentRingsTableProps) => {
  const theme = useTheme();
  const navigate = useNavigate();

  const columns: DataTableColumn<
    DeploymentRingResultantVersion
  >[] = useMemo(() => {
    const navigateToRollout = (deploymentRingId: string, rolloutId: string) =>
      navigate(generatePath(ROLLOUT_PATH, { deploymentRingId, rolloutId }));

    return [
      {
        key: "deploymentRingDisplayName",
        name: "Deployment Ring",
        minWidth: 200,
        maxWidth: 260,
        isResizable: true,
        isSorted: true,
        sort: true,
        onRender: (item: DeploymentRingResultantVersion) => (
          <Link
            onClick={() =>
              navigate(`/system/plt/deployment-rings/${item.deploymentRingId}`)
            }
          >
            {item.deploymentRingDisplayName}
          </Link>
        )
      },
      {
        key: "deploymentRingCode",
        name: "Code",
        minWidth: 120,
        maxWidth: 150,
        isResizable: true,
        sort: true,
        onRender: (item: DeploymentRingResultantVersion) => (
          <Text>{item.deploymentRingCode}</Text>
        )
      },
      {
        key: "version",
        name: "Resultant Version",
        minWidth: 180,
        maxWidth: 200,
        isResizable: true,
        sort: true,
        onRender: (item: DeploymentRingResultantVersion) =>
          item.version ? (
            <Stack
              horizontal
              verticalAlign="center"
              tokens={{ childrenGap: theme.spacing.s1 }}
            >
              <TextBadge
                badgeSize={TextBadgeSize.small}
                badgeColor={TextBadgeColor.blue}
              >
                {item.version}
              </TextBadge>
              <TextBadge
                badgeSize={TextBadgeSize.small}
                badgeColor={
                  item.isLatest ? TextBadgeColor.green : TextBadgeColor.yellow
                }
              >
                {item.isLatest ? "Latest" : "Outdated"}
              </TextBadge>
            </Stack>
          ) : (
            <Text>None</Text>
          )
      },
      {
        key: "rolloutDisplayName",
        name: "From Rollout",
        minWidth: 200,
        maxWidth: 260,
        isResizable: true,
        sort: true,
        onRender: (item: DeploymentRingResultantVersion) => {
          if (item.rolloutId) {
            return (
              <Link
                onClick={() =>
                  navigateToRollout(item.deploymentRingId, item.rolloutId!)
                }
              >
                {item.rolloutDisplayName}
              </Link>
            );
          }

          // Not in any rollout; included because another package depends on it
          return item.isDependency ? (
            <TextBadge
              badgeSize={TextBadgeSize.small}
              badgeColor={TextBadgeColor.grey}
            >
              Dependency
            </TextBadge>
          ) : null;
        }
      },
      {
        key: "installStartUtc",
        name: "Installing Since",
        minWidth: 200,
        maxWidth: 200,
        isResizable: true,
        sort: true,
        onRender: (item: DeploymentRingResultantVersion) =>
          item.installStartUtc ? (
            <Stack
              wrap
              styles={{ root: { whiteSpace: "initial" } }}
              horizontal
              verticalAlign="center"
              tokens={{ childrenGap: theme.spacing.s1 }}
            >
              <Text>{item.installStartUtc.toDayDefaultFormat()}</Text>
              <TextBadge
                badgeSize={TextBadgeSize.small}
                badgeColor={TextBadgeColor.grey}
              >
                {item.installStartUtc.toRelative()}
              </TextBadge>
            </Stack>
          ) : null
      },
      {
        key: "scheduledVersion",
        name: "Scheduled Version",
        minWidth: 200,
        maxWidth: 240,
        isResizable: true,
        sort: true,
        onRender: (item: DeploymentRingResultantVersion) =>
          item.scheduledVersion ? (
            <Stack
              wrap
              styles={{ root: { whiteSpace: "initial" } }}
              horizontal
              verticalAlign="center"
              tokens={{ childrenGap: theme.spacing.s1 }}
            >
              <Link
                onClick={() =>
                  navigateToRollout(
                    item.deploymentRingId,
                    item.scheduledRolloutId!
                  )
                }
              >
                {item.scheduledVersion}
              </Link>
              <TextBadge
                badgeSize={TextBadgeSize.small}
                badgeColor={TextBadgeColor.grey}
              >
                {item.scheduledInstallStartUtc?.toRelative()}
              </TextBadge>
            </Stack>
          ) : null
      }
    ];
  }, [navigate, theme.spacing.s1]);

  return (
    <DataTable
      stickyHeader
      shimmerLines={10}
      columns={columns}
      onShouldVirtualize={() => true}
      {...props}
    />
  );
};
